import { FC, useEffect, useRef, useState } from "react";
import { AxiosError } from "axios";
import { saveAs } from "file-saver";

import { Button, notification, Tag } from "antd";

import styles from "./ReportActions.module.scss";

import ChoseExamination from "@/modules/Report/components/ChoseExamination";
import { IExamination } from "@/models/Examination";
import { useAppSelector } from "@/hooks/redux";
import ReportsApi from "@/api/rest/reports";
import b64DecodeUnicode from "@/utils/base64ToUtf8";

interface ReportActionsProps {
  isFinished: boolean;
  createLoading: boolean;
  deleteLoading: boolean;
  chosenExamination: IExamination | null;
  onChooseExamination: (examination: IExamination) => void;
  onCopy: () => void;
  onNewExam: () => void;
  onSave: () => void;
  onDelete: () => void;
  onActivate: () => void;
  onDeactivate: () => void;
}

const ReportActions: FC<ReportActionsProps> = ({
  createLoading,
  deleteLoading,
  chosenExamination,
  onChooseExamination,
  onCopy,
  onNewExam,
  onSave,
  onDelete,
  onActivate,
  onDeactivate,
  isFinished,
}) => {
  const { activeExamination, m15 } = useAppSelector(
    (state) => state.currentDataReducer
  );
  const userType = useAppSelector((state) => state.userReducer.userType);

  const lastActiveExamination = useRef<IExamination>();
  const [areProtocolsReady, setAreProtocolsReady] = useState(false);

  useEffect(() => {
    if (activeExamination) {
      lastActiveExamination.current = activeExamination;
    }
  }, [activeExamination]);

  useEffect(() => {
    let interval: ReturnType<typeof setInterval>;
    if (lastActiveExamination.current && isFinished) {
      interval = setInterval(async () => {
        if (lastActiveExamination.current) {
          const areReady = await ReportsApi.checkReadiness(
            lastActiveExamination.current.examId
          );
          if (areReady) {
            setAreProtocolsReady(true);
            clearInterval(interval);
          }
        }
      }, 1000);
    } else {
      setAreProtocolsReady(false);
    }

    return () => {
      clearInterval(interval);
    };
  }, [isFinished]);

  const handleReport = async (type: "test-report" | "test-data") => {
    try {
      const examId =
        chosenExamination?.examId || lastActiveExamination.current?.examId;
      if (examId) {
        const { blob, filename } =
          type === "test-report"
            ? await ReportsApi.getTestReport(examId)
            : await ReportsApi.getTestDataReport(examId);
        saveAs(blob, `${b64DecodeUnicode(filename)}.xlsx`);
      }
    } catch (e) {
      if (e instanceof AxiosError && e.response?.status === 425) {
        notification.open({
          type: "error",
          message: "Protocol is not ready yet",
        });
      }
    }
  };

  const handleTestReport = async () => {
    await handleReport("test-report");
  };

  const handleTestData = async () => {
    await handleReport("test-data");
  };

  const isAdmin = userType === "admin";
  const protocolButtonsDisabled =
    chosenExamination?.status === "finished" ? false : !areProtocolsReady;
  return (
    <div className={styles.container}>
      <ChoseExamination onChoose={onChooseExamination} />
      <Button disabled={!chosenExamination} onClick={onCopy}>
        Copy
      </Button>
      <Button
        disabled={createLoading}
        loading={createLoading}
        onClick={onNewExam}
      >
        New exam
      </Button>
      <Button disabled={createLoading} loading={createLoading} onClick={onSave}>
        Save
      </Button>
      <Button
        disabled={deleteLoading || !chosenExamination}
        loading={deleteLoading}
        onClick={onDelete}
      >
        Delete
      </Button>
      {activeExamination && !m15 ? (
        <Button type="primary" onClick={onDeactivate}>
          Deactivate
        </Button>
      ) : (
        <Button
          disabled={
            !chosenExamination || chosenExamination.status === "finished"
          }
          type="primary"
          onClick={onActivate}
        >
          Activate
        </Button>
      )}
      {(isFinished || chosenExamination?.status === "finished") && (
        <>
          <Button disabled={protocolButtonsDisabled} onClick={handleTestReport}>
            Create test report
          </Button>
          {isAdmin && (
            <Button disabled={protocolButtonsDisabled} onClick={handleTestData}>
              Create test data
            </Button>
          )}
        </>
      )}
      {activeExamination ? (
        <Tag color="green">
          Active examination name: {activeExamination.sampleName}
        </Tag>
      ) : (
        <Tag color="orange">No examination is active</Tag>
      )}
    </div>
  );
};

export default ReportActions;
